/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { sendEvent } from '@services/ga/send-event';
import { undoImport } from '../api/spreadsheets.api';
import {
  readResultSummary,
  type ImportResultSummary,
} from '../components/import-result-state';

export interface UseImportResultBanner {
  /** The strip's content, or `null` when there is no fresh import to announce. */
  summary: ImportResultSummary | null;
  /** Whether the listing is currently narrowed to this import. */
  onlyThisImport: boolean;
  toggleOnlyThisImport: () => void;
  undo: () => void;
  dismiss: () => void;
}

export interface UseImportResultBannerOptions {
  agencyId: string;
  /** The listing's current `importId` filter value, if any. */
  importFilter: string;
  /** Applies the listing's `importId` filter; `''` clears it. */
  setImportFilter: (importId: string) => void;
  /** Reloads the listing after an undo removed records from it. */
  onRefresh: () => void;
}

/**
 * Runs the undo request for the current summary and reports the outcome,
 * then dismisses the strip and refreshes the listing on success.
 */
async function performUndo(
  args: {
    agencyId: string;
    summary: ImportResultSummary | null;
    t: (key: string) => string;
    dismiss: () => void;
    onRefresh: () => void;
  },
): Promise<void> {
  const { agencyId, summary, t, dismiss, onRefresh } = args;
  if (!summary) return;
  try {
    await undoImport(agencyId, summary.importId);
    sendEvent('spreadsheet_import_undone', { import_id: summary.importId });
    toast.success(t('banner.undoDone'));
    dismiss();
    onRefresh();
  } catch {
    toast.error(t('banner.undoFailed'));
  }
}

/**
 * Drives the post-import strip on a listing.
 *
 * @param options - Agency, the listing's import filter, and its refresh hook.
 * @returns The strip's state and its three actions.
 */
export function useImportResultBanner(
  options: UseImportResultBannerOptions,
): UseImportResultBanner {
  const { agencyId, importFilter, setImportFilter, onRefresh } = options;
  const { t } = useTranslation('features/spreadsheets');
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const importId = searchParams.get('import');

  const [summary, setSummary] = useState<ImportResultSummary | null>(
    () => readResultSummary(location.state, importId),
  );

  const clearParam = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('import');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // A refresh keeps `?import=` but drops the navigation state that carried
  // the counts. Rather than render a strip with blanks in it, drop the param.
  useSyncSummaryFromState({ importId, state: location.state, clearParam, setSummary });

  const onlyThisImport = isNarrowedToImport(importId, importFilter);

  const dismiss = useCallback(() => {
    if (onlyThisImport) setImportFilter('');
    clearParam();
    setSummary(null);
  }, [onlyThisImport, setImportFilter, clearParam]);

  const undo = useCallback(
    () => performUndo({ agencyId, summary, t, dismiss, onRefresh }),
    [agencyId, summary, t, dismiss, onRefresh],
  );

  return {
    summary,
    onlyThisImport,
    toggleOnlyThisImport: () => setImportFilter(onlyThisImport ? '' : (importId ?? '')),
    undo: () => { undo(); },
    dismiss,
  };
}

/**
 * Whether the listing is currently narrowed to the given import.
 */
function isNarrowedToImport(importId: string | null, importFilter: string): boolean {
  return Boolean(importId) && importFilter === importId;
}

/**
 * Keeps `summary` in sync with the navigation state and drops a stale
 * `?import=` param when the counts are no longer available (e.g. after a
 * refresh).
 */
function useSyncSummaryFromState(args: {
  importId: string | null;
  state: unknown;
  clearParam: () => void;
  setSummary: (s: ImportResultSummary | null) => void;
}): void {
  const { importId, state, clearParam, setSummary } = args;
  useEffect(() => {
    if (!importId) { setSummary(null); return; }
    const restored = readResultSummary(state, importId);
    setSummary(restored);
    if (!restored) clearParam();
  }, [importId, state, clearParam]);
}
